import React, { useState, useRef, ReactElement, MutableRefObject, ChangeEvent, useMemo } from 'react';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RadioOrSelectOption } from '../../../../../types/common.types';
import { Collection, Resource } from '../../../../../types/entity.types';

import useToaster, { ToasterActions } from '../../../../../hooks/use-toaster/useToaster';

import { getClassNames } from '../../../../../utils/util';
import { ARCHIVED, ERROR } from '../../../../../utils/constants';

import Button from '../../../../common/button/Button';
import Modal from '../../../../common/modal/Modal';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import TextInput from '../../../../common/input/text-input/TextInput';
import Dropdown from '../../../../common/input/dropdown/Dropdown';

import { ReactComponent as FileIcon } from '../../../../../resources/icons/file.svg';
import { ReactComponent as UploadIcon } from '../../../../../resources/icons/upload.svg';
import { ReactComponent as EditIcon } from '../../../../../resources/icons/edit.svg';

import './accountability-nutrition.scss';
import '../accountability.scss';

type Props = {
  status: string;
  collections: Collection[];
  mealPlans: Resource[];
  createMealPlan: (collectionExternalReference: string, mealPlan: Resource) => void;
  updateMealPlan: (resource: Resource) => void;
  deleteMealPlan: (collectionExternalReference: string, uuid: string) => void;
  downloadDocument: (resource: Resource) => void;
};

const AccountabilityNutrition = ({
  status,
  collections,
  mealPlans,
  createMealPlan,
  updateMealPlan,
  deleteMealPlan,
  downloadDocument
}: Props): ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [diagram, setDiagram] = useState<File | undefined>(undefined);
  const [collection, setCollection] = useState<Collection | undefined>(undefined);
  const [selectedMealPlan, setSelectedMealPlan] = useState<Resource | undefined>(undefined);

  const fileRef: MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  const { showToaster }: ToasterActions = useToaster();

  const collectionsForDropdown: RadioOrSelectOption[] = useMemo((): RadioOrSelectOption[] => {
    return collections.map(
      (collection: Collection): RadioOrSelectOption => ({
        label: collection.name,
        value: collection.externalReference as string
      })
    );
  }, [collections]);

  const handleAddMealPlanClick = (): void => {
    setName('');
    setDiagram(undefined);
    setCollection(undefined);
    setSelectedMealPlan(undefined);
    setShowModal(true);
  };

  const handleEditMealPlanClick = (mealPlan: Resource): void => {
    setName(mealPlan.displayName ?? '');
    setDiagram(undefined);
    setCollection({
      name: mealPlan.collectionName as string,
      externalReference: mealPlan.collectionExternalReference as string
    });
    setSelectedMealPlan(mealPlan);
    setShowModal(true);
  };

  const handleSaveMealPlanClick = async (): Promise<void> => {
    if (selectedMealPlan) {
      const resource: Resource = {
        ...selectedMealPlan,
        displayName: name,
        linkedMediaType: 'Document'
      };

      if (diagram) {
        resource.name = diagram.name;
        resource.document = diagram;
      }

      updateMealPlan(resource);
    } else {
      if (!diagram) {
        showToaster(ERROR, 'Please select a file.');
        return;
      }

      if (!collection) {
        showToaster(ERROR, 'Please select a collection.');
        return;
      }

      const resource: Resource = {
        pinned: false,
        documentType: 'meal-plan',
        linkedMediaType: 'Document',
        displayName: name,
        name: diagram.name,
        document: diagram
      };

      createMealPlan(collection.externalReference as string, resource);
    }
    setShowModal(false);
  };

  const handleDownloadMealPlanClick = (mealPlan: Resource): void => {
    downloadDocument(mealPlan);
  };

  const handleDeleteMealPlanClick = (): void => {
    if (!selectedMealPlan) return;

    deleteMealPlan(selectedMealPlan.collectionExternalReference as string, selectedMealPlan.uuid as string);
    setShowModal(false);
  };

  const handleCollectionChange = (option: RadioOrSelectOption): void => {
    setCollection(collections.find((collection: Collection): boolean => collection.externalReference === option.value));
  };

  const renderMealPlans = (): ReactElement[] => {
    return mealPlans.map(
      (mealPlan: Resource): ReactElement => (
        <div
          className={getClassNames('accountability-nutrition__meal-plans__plan', { disabled: status === ARCHIVED })}
          key={mealPlan.uuid}
          data-testid="accountability-meal-plan"
        >
          <div>
            <p>{mealPlan.displayName}</p>
          </div>
          <div>
            <p>{mealPlan.name}</p>
          </div>
          <div className="accountability-nutrition__meal-plans__plan__icons">
            <UploadIcon
              className="accountability-nutrition__download-icon"
              onClick={(): void => handleDownloadMealPlanClick(mealPlan)}
              data-testid="download-icon"
            />
            {status !== ARCHIVED && (
              <EditIcon
                className="accountability-nutrition__edit-icon"
                onClick={(): void => handleEditMealPlanClick(mealPlan)}
                data-testid="edit-icon"
              />
            )}
          </div>
        </div>
      )
    );
  };

  return (
    <div
      className={getClassNames('accountability__section', {
        closed: !open
      })}
      data-testid="accountability-nutrition"
    >
      <div className="accountability__section__header">
        <p>NUTRITION</p>
        {!open && (
          <FontAwesomeIcon
            className="accountability__section__open-icon"
            icon={faPlus}
            onClick={(): void => setOpen(true)}
            data-testid="open-icon"
          />
        )}
        {open && (
          <FontAwesomeIcon
            className="accountability__section__close-icon"
            icon={faMinus}
            onClick={(): void => setOpen(false)}
            data-testid="close-icon"
          />
        )}
      </div>
      {open && (
        <div className="accountability-nutrition">
          <div className="accountability-nutrition__meal-plans">
            {mealPlans.length > 0 && (
              <div className="accountability-nutrition__meal-plans__headers">
                <p>Meal Plan</p>
                <p>Diagram</p>
              </div>
            )}
            {renderMealPlans()}
          </div>
          {status !== ARCHIVED && <Button text="Add" onClick={handleAddMealPlanClick} />}
        </div>
      )}

      <Modal show={showModal}>
        <div className="accountability-nutrition__modal">
          <div className="accountability-nutrition__modal__header">Add Meal Plan</div>
          <div className="accountability-nutrition__modal__input-container">
            <TextInput
              label="Meal Plan Name"
              name="meal-plan-name"
              value={name}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void => setName(target.value)}
            />
            <FilePicker
              triggerElement={
                <div className="accountability-nutrition__modal__file-input">
                  <FileIcon />
                  <input
                    type="text"
                    value={diagram ? diagram.name : ''}
                    placeholder="Click button to upload"
                    disabled
                  />
                  <div
                    className="accountability-nutrition__modal__file-input__icon"
                    onClick={(): void => fileRef.current?.click()}
                  >
                    <UploadIcon data-testid="upload-icon" />
                  </div>
                </div>
              }
              fileRef={fileRef}
              onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
                setDiagram(target.files ? target.files[0] : undefined)
              }
            />
            {!selectedMealPlan && (
              <Dropdown
                name="meal-plan-collection"
                value={
                  collection?.externalReference
                    ? { label: collection.name, value: collection.externalReference }
                    : undefined
                }
                options={collectionsForDropdown}
                onChange={handleCollectionChange}
                placeholder="Select Collection..."
              />
            )}
          </div>
          <div className="accountability-nutrition__modal__button-container">
            <Button text="Cancel" onClick={(): void => setShowModal(false)} />
            {selectedMealPlan && status !== ARCHIVED && <Button text="Delete" onClick={handleDeleteMealPlanClick} />}
            <Button text="Save" onClick={handleSaveMealPlanClick} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AccountabilityNutrition;
